import { CSSProperties, type ReactNode, useCallback } from 'react';

import { Tooltip, useSnackbar } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { EllipsisTypography } from '@/styles.ts';

type EllipsisFormFieldWithCopyToClipboardProps = {
  style?: CSSProperties | undefined;
  tooltip?: string | ReactNode;
  value: string | undefined;
  valueName: string;
};

export const EllipsisFormFieldWithCopyToClipboard = ({
  style = {},
  tooltip,
  value,
  valueName,
}: EllipsisFormFieldWithCopyToClipboardProps) => {
  const { t } = useTranslation(['deviceConfig', 'eventStream']);
  const { enqueueSnackbar } = useSnackbar();

  const handleCopyToClipboard = useCallback(
    (value: string | undefined) => {
      if (!value) return;
      navigator.clipboard
        .writeText(value)
        .then(() => {
          enqueueSnackbar(t('eventStream:copiedToClipboard', { value: valueName }), {
            variant: 'success',
            autoHideDuration: 1500,
            key: `${valueName}_clipboard`,
          });
        })
        .catch(() => {
          enqueueSnackbar(
            t('eventStream:copyToClipboardFailed', {
              defaultValue: 'Failed to copy {{value}} to clipboard',
              value: valueName,
            }),
            {
              variant: 'alarm',
              autoHideDuration: 1500,
              key: `${valueName}_clipboard_error`,
            }
          );
        });
    },
    [enqueueSnackbar, t, valueName]
  );

  return (
    <Tooltip tooltip={tooltip || value}>
      <EllipsisTypography
        onDoubleClick={() => handleCopyToClipboard(value)}
        style={{
          ...style,
        }}
      >
        {value}
      </EllipsisTypography>
    </Tooltip>
  );
};
